<template>
  <div class="module-page">
    <!-- 搜索工具条 -->
    <el-card shadow="never" class="toolbar-card">
      <div class="toolbar">
        <el-input
          v-model="keyword"
          :placeholder="`输入${searchLabel}搜索`"
          clearable
          class="keyword-input"
          @keyup.enter="handleSearch"
          @clear="handleSearch"
        />
        <el-button type="primary" :icon="Search" @click="handleSearch">查询</el-button>
        <el-button :icon="Refresh" @click="handleReset">重置</el-button>
        <div class="toolbar-right">
          <el-button type="success" :icon="Plus" @click="openDialog()">新增</el-button>
          <el-button plain :icon="Download" @click="handleExport">导出</el-button>
        </div>
      </div>
    </el-card>

    <!-- 数据表格 -->
    <el-card shadow="never" class="table-card">
      <template #header>
        <span class="table-title">{{ moduleConf.name }}</span>
        <span class="table-tip">数据来源：接口 /api/{{ moduleConf.key }}/page</span>
      </template>
      <el-table :data="rows" v-loading="loading" border stripe>
        <el-table-column type="index" label="#" width="50" align="center" />
        <el-table-column
          v-for="col in moduleConf.columns"
          :key="col.prop"
          :prop="col.prop"
          :label="col.label"
          :width="col.width"
          :min-width="col.minWidth || 110"
          show-overflow-tooltip
        >
          <template #default="{ row }">
            <el-tag v-if="col.type === 'tag'" :type="tagType(row[col.prop])" effect="light">
              {{ row[col.prop] || '-' }}
            </el-tag>
            <span v-else-if="col.type === 'amount'" class="amount">
              {{ formatAmount(row[col.prop]) }}
            </span>
            <span v-else>{{ row[col.prop] === '' || row[col.prop] == null ? '-' : row[col.prop] }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="130" align="center" fixed="right">
          <template #default="{ row }">
            <el-button link type="primary" @click="openDialog(row)">编辑</el-button>
            <el-button link type="danger" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pagination"
        v-model:current-page="pageNum"
        v-model:page-size="pageSize"
        :total="total"
        :page-sizes="[10, 20, 50]"
        layout="total, sizes, prev, pager, next, jumper"
        background
        @change="fetchData"
      />
    </el-card>

    <!-- 新增/编辑弹窗:表单项由模块列配置自动生成 -->
    <el-dialog
      v-model="dialogVisible"
      :title="`${form.id ? '编辑' : '新增'}${moduleConf.name}`"
      width="680px"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-form ref="formRef" :model="form" :rules="rules" label-width="120px">
        <el-row :gutter="16">
          <el-col v-for="col in moduleConf.columns" :key="col.prop" :span="isWide(col) ? 24 : 12">
            <el-form-item :label="col.label" :prop="col.prop">
              <el-input-number
                v-if="col.type === 'number'"
                v-model="form[col.prop]"
                :controls="false"
                class="full"
                :placeholder="`请输入${col.label}`"
              />
              <el-input-number
                v-else-if="col.type === 'amount'"
                v-model="form[col.prop]"
                :controls="false"
                :precision="2"
                :min="0"
                class="full"
                :placeholder="`请输入${col.label}`"
              />
              <el-date-picker
                v-else-if="col.type === 'date'"
                v-model="form[col.prop]"
                type="date"
                value-format="YYYY-MM-DD"
                class="full"
                :placeholder="`请选择${col.label}`"
              />
              <el-date-picker
                v-else-if="col.type === 'datetime'"
                v-model="form[col.prop]"
                type="datetime"
                value-format="YYYY-MM-DD HH:mm:ss"
                class="full"
                :placeholder="`请选择${col.label}`"
              />
              <el-select
                v-else-if="col.options"
                v-model="form[col.prop]"
                filterable
                allow-create
                default-first-option
                clearable
                class="full"
                :placeholder="`请选择或输入${col.label}`"
              >
                <el-option v-for="opt in col.options" :key="opt" :label="opt" :value="opt" />
              </el-select>
              <el-input
                v-else-if="isWide(col)"
                v-model="form[col.prop]"
                type="textarea"
                :rows="2"
                maxlength="255"
                show-word-limit
                :placeholder="`请输入${col.label}`"
              />
              <el-input
                v-else
                v-model="form[col.prop]"
                maxlength="128"
                clearable
                :placeholder="`请输入${col.label}`"
              />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="handleSave">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Search, Refresh, Plus, Download } from '@element-plus/icons-vue'
import { findModule } from '@/config/modules'
import { moduleApi } from '@/api'

const route = useRoute()
const moduleConf = computed(() => findModule(route.meta.moduleKey) || { key: '', name: '', columns: [], searchProp: '' })

const rows = ref([])
const total = ref(0)
const loading = ref(false)
const saving = ref(false)
const keyword = ref('')
const pageNum = ref(1)
const pageSize = ref(10)

const dialogVisible = ref(false)
const formRef = ref()
// 表单模型按列配置动态填充,故不预先声明字段
const form = reactive({})

// 搜索框提示语取查询字段对应的列名,未配置时退化为“关键字”
const searchLabel = computed(() => {
  const col = moduleConf.value.columns.find(c => c.prop === moduleConf.value.searchProp)
  return col ? col.label : '关键字'
})

const rules = computed(() => {
  const result = {}
  moduleConf.value.columns.forEach(col => {
    if (!col.required) return
    const isNumber = col.type === 'number' || col.type === 'amount'
    result[col.prop] = [{
      required: true,
      type: isNumber ? 'number' : 'string',
      message: `请输入${col.label}`,
      trigger: isNumber ? 'change' : 'blur'
    }]
  })
  return result
})

function isWide(col) {
  // 长文本(备注、情况等)独占一行并使用文本域
  return (col.type == null || col.type === 'text') && (col.minWidth || 0) >= 140
}

function buildParams(pageNumValue, pageSizeValue) {
  const params = { pageNum: pageNumValue, pageSize: pageSizeValue }
  const kw = keyword.value.trim()
  if (kw && moduleConf.value.searchProp) {
    params[moduleConf.value.searchProp] = kw
  }
  return params
}

async function fetchData() {
  if (!moduleConf.value.key) return
  loading.value = true
  try {
    const data = await moduleApi(moduleConf.value.key).page(buildParams(pageNum.value, pageSize.value))
    rows.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

function handleSearch() {
  pageNum.value = 1
  fetchData()
}

function handleReset() {
  keyword.value = ''
  pageNum.value = 1
  fetchData()
}

function openDialog(row) {
  const columns = moduleConf.value.columns
  Object.keys(form).forEach(key => delete form[key])
  columns.forEach(col => {
    const isNumber = col.type === 'number' || col.type === 'amount'
    const value = row ? row[col.prop] : null
    if (value == null || value === '') {
      form[col.prop] = isNumber ? null : ''
    } else {
      form[col.prop] = isNumber ? Number(value) : value
    }
  })
  form.id = row ? row.id : undefined
  dialogVisible.value = true
  // 清除上次校验残留
  formRef.value?.clearValidate()
}

async function handleSave() {
  const valid = await formRef.value.validate().catch(() => false)
  if (!valid) return
  saving.value = true
  try {
    const api = moduleApi(moduleConf.value.key)
    const payload = { ...form }
    if (payload.id) {
      await api.update(payload)
    } else {
      await api.add(payload)
    }
    ElMessage.success('保存成功')
    dialogVisible.value = false
    fetchData()
  } finally {
    saving.value = false
  }
}

async function handleDelete(row) {
  // 取消确认时 ElMessageBox reject,直接中断流程
  const confirmed = await ElMessageBox.confirm(`确定删除${moduleConf.value.name}该条记录吗？`, '提示', {
    type: 'warning',
    confirmButtonText: '确定',
    cancelButtonText: '取消'
  }).catch(() => false)
  if (!confirmed) return
  await moduleApi(moduleConf.value.key).remove(row.id)
  ElMessage.success('删除成功')
  // 删除末页最后一条时回退一页
  if (rows.value.length === 1 && pageNum.value > 1) {
    pageNum.value -= 1
  }
  fetchData()
}

// 导出当前查询条件下的全部数据(一次性取大页,前端转 CSV)
async function handleExport() {
  const columns = moduleConf.value.columns
  const data = await moduleApi(moduleConf.value.key).page(buildParams(1, 5000))
  const list = data.list || []
  if (!list.length) {
    ElMessage.warning('暂无可导出的数据')
    return
  }
  const escape = val => `"${String(val == null ? '' : val).replace(/"/g, '""')}"`
  const lines = [
    columns.map(col => escape(col.label)).join(','),
    ...list.map(row => columns.map(col => escape(row[col.prop])).join(','))
  ]
  // 头部 BOM,保证 Excel 打开不乱码
  const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${moduleConf.value.name}_${new Date().toISOString().slice(0, 10)}.csv`
  link.click()
  URL.revokeObjectURL(url)
  ElMessage.success(`已导出 ${list.length} 条记录`)
}

// 状态标签颜色映射
function tagType(val) {
  const s = String(val)
  if (['优秀', '良好', '正常', '合格', '已售', '部分销售', '已免疫', '入库', '增强'].includes(s)) return 'success'
  if (['差', '异常', '不合格', '不足', '偏弱', '待免疫'].includes(s)) return 'danger'
  if (['一般', '待售', '待检', '略差'].includes(s)) return 'warning'
  return 'info'
}

function formatAmount(val) {
  const n = Number(val)
  return Number.isNaN(n) ? val : n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// 切换模块路由时重新加载
watch(() => route.meta.moduleKey, () => {
  keyword.value = ''
  pageNum.value = 1
  pageSize.value = 10
  dialogVisible.value = false
  fetchData()
})

onMounted(fetchData)
</script>

<style scoped>
.module-page {
  min-height: 100%;
}
.toolbar-card {
  margin-bottom: 16px;
}
.toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.keyword-input {
  width: 240px;
}
.toolbar-right {
  margin-left: auto;
  display: flex;
  gap: 8px;
}
.table-title {
  font-weight: 600;
}
.table-tip {
  margin-left: 12px;
  font-size: 12px;
  color: #909399;
}
.amount {
  color: #e6a23c;
  font-weight: 500;
}
.full {
  width: 100%;
}
.pagination {
  margin-top: 16px;
  justify-content: flex-end;
}
@media (max-width: 768px) {
  .toolbar-right {
    margin-left: 0;
  }
  .keyword-input {
    width: 100%;
  }
}
</style>
